import React, { useContext, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@redux/store'
import { hydrateArtwork } from '@redux/modules/artwork'

import { toast } from 'react-toastify'

import { useRealtimeSubscription } from '@domain-group/react-hooks'

import {
  Box, Columns, DashboardModule, Form, Grid, InputSearch, LoadingModule, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { launchModal } from '@domain-group/javascript-utils'

import api, { baseURL } from '@functions/api'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useArtwork from '@hooks/useArtwork'
import useOrderItem from '@hooks/useOrderItem'

import RealtimeContext from '@contexts/realtimeContext'
import PageContext from '@contexts/pageContext'

import type { ArtworkModel } from '@models/types'

import CanvaTemplatePage from './components/CanvaTemplatePage'

type AblyPayload = {
  success: boolean,
  payload: {
        edit_url?: string,
        artwork: ArtworkModel,
      },
}

const MODAL_KEY = 'CanvaTemplateSelectionModal'

type ModalCallbacks = {
  approveCampaignQuote: (payload: HandleCallbackActionParams) => void,
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

type CanvaTemplate = {
  continuation?: string,
  create_url: string,
  created_at: number,
  id: string,
  thumbnail: {
    width: number,
    height: number,
    url: string,
  },
  title: string,
  updated_at: number,
  view_url: string,
}

const hydrateArtworkWithCanvaData = async (artwork: ArtworkModel, dispatch: AppDispatch) => {
  if (artwork?.id){
    await dispatch(hydrateArtwork(artwork))
  }
}

const processCanvaArtworkRedirect = ({
  data,
  callbacks,
}: {
  data: AblyPayload,
  callbacks: {
    closeModal: () => void,
    closeLoadingModal: () => void,
    showLoadingModal: (payload: object) => void,
    dispatch: AppDispatch,
    setCreatingArtwork: React.Dispatch<React.SetStateAction<boolean>>,
    setSelectedTemplateId: React.Dispatch<React.SetStateAction<string | null>>,
  },
}) => {
  const {
    closeLoadingModal, closeModal, dispatch, setCreatingArtwork, setSelectedTemplateId, showLoadingModal,
  } = callbacks

  const { success, payload } = data
  const { edit_url: link = '', artwork } = payload || {}

  if (success){
    showLoadingModal({
      isAblySuccess: true,
      modalTitle: 'Creating',
      modalTitleSecondLine: 'Canva artwork',
    })
    window.open(link, '_blank')
    hydrateArtworkWithCanvaData(artwork, dispatch)
    setCreatingArtwork(false)
    setSelectedTemplateId(null)
    window.setTimeout(() => {
      closeLoadingModal()
      closeModal()
    }, 900)
  }

  if (!success){
    closeLoadingModal()
    toast.error('Artwork creation failed. Please try again.')
    setCreatingArtwork(false)
    setSelectedTemplateId(null)
  }
}

const TEMPLATE_PAGE_LIMIT = 25

const CanvaTemplateSelectionModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks

  const [loading, setLoading] = useState(false)
  const [creatingArtwork, setCreatingArtwork] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [continuation, setContinuation] = useState<string | null>(null)
  const [nextContinuation, setNextContinuation] = useState<string | null>(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [templates, setTemplates] = useState<CanvaTemplate[]>([])

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext
  const modalPayload = modalData?.[MODAL_KEY] || {}

  const { agency, orderItem: initOrderItem } = modalPayload
  const agencyId = agency?.id

  const { callbacks: pageCallbacks } = useContext(PageContext)
  const { closeCanvaArtworkingLoadingModal } = pageCallbacks || {}

  const dispatch = useDispatch()

  const { campaign, orderItem: { id: orderItemId, service_product_id } } = useOrderItem(initOrderItem)

  const { id: campaignId } = campaign || {}

  const { callbacks: { createArtwork } } = useArtwork({
    campaign_id: campaignId || undefined,
    order_item_id: initOrderItem?.id,
    service_product_id,
  })

  useEffect(() => {
    if (!showModal && closeCanvaArtworkingLoadingModal){
      closeCanvaArtworkingLoadingModal()
    }
  }, [showModal])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(query)
    }, 350)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [query])

  useEffect(() => {
    setContinuation(null)
    setNextContinuation(null)
  }, [agencyId, debouncedQuery])

  useEffect(() => {
    if (!agencyId){
      return
    }

    const fetchBrandTemplates = async () => {
      try {
        setLoading(true)

        const normalizedQuery = debouncedQuery.trim().toLowerCase()

        const requestOptions: Record<string, unknown> = normalizedQuery
          ? { agency_id: agencyId, limit: TEMPLATE_PAGE_LIMIT, query: normalizedQuery }
          : { agency_id: agencyId, limit: TEMPLATE_PAGE_LIMIT }

        if (continuation){
          requestOptions.continuation = continuation
        }

        const { data } = await api(`${baseURL}/canva/get_brand_templates`, requestOptions)
        let templatesFromResponse: CanvaTemplate[] = []

        if (Array.isArray(data)){
          templatesFromResponse = data
        } else if (Array.isArray((data as { templates?: CanvaTemplate[] })?.templates)){
          templatesFromResponse = (data as { templates?: CanvaTemplate[] }).templates || []
        }

        const continuationFromResponse = templatesFromResponse[0]?.continuation || null

        setNextContinuation(continuationFromResponse)
        setCanLoadMore(!!continuationFromResponse)

        setTemplates((previousTemplates) => {
          if (!continuation){
            return templatesFromResponse
          }

          return [...previousTemplates, ...templatesFromResponse]
        })
      } catch {
        // Ignore for now until template selection UI is wired.
        if (!continuation){
          setTemplates([])
        }
        setNextContinuation(null)
        setCanLoadMore(false)
      } finally {
        setLoading(false)
      }
    }

    fetchBrandTemplates()
  }, [agencyId, continuation, debouncedQuery])

  const loadMore = () => {
    if (loading || !canLoadMore){
      return
    }

    setContinuation(nextContinuation)
  }

  const initialLoading = loading && !continuation
  const hasTemplates = templates.length > 0

  const realtimeContext = useContext(RealtimeContext)
  // @ts-expect-error TS2339
  const { clientRef } = realtimeContext

  // Realtime/Websocket Connection
  useRealtimeSubscription({
    apiKey: process.env.REACT_APP_ABLY_PUBLIC_KEY,
    callbacks: {
      success: (data: AblyPayload) => processCanvaArtworkRedirect({
        data,
        callbacks: {
          closeLoadingModal: closeCanvaArtworkingLoadingModal,
          closeModal,
          dispatch,
          showLoadingModal: (payload: object) => launchModal({
            callbacks: pageCallbacks,
            modalKey: 'CanvaArtworkingLoadingModal',
            payload,
          }),
          setCreatingArtwork,
          setSelectedTemplateId,
        },
      }),
    },
    channelName: 'orders',
    clientRef,
    eventName: `order_item.${orderItemId}.canva.design.created`,
  }, [orderItemId])

  return (
    <SidebarModal
      callbacks={callbacks}
      disableAnimation
      disableOverlay
      modalKey={MODAL_KEY}
      showModal={showModal}
      size="fullscreen"
    >
      <SidebarModal.Header callbacks={callbacks} title="Select" titleSecondLine="Template" />

      <SidebarModal.Content boxProps={{ backgroundColor: 'backgroundColor' }} hasFooter={false}>
        <Columns flexDirection={['column-reverse', 'column-reverse', 'row']}>
          <Columns.Main>

            <Form.Field
              direction="column"
              label="Filter by Title"
            >
              {/* @ts-expect-error TS2741 */}
              <InputSearch
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                value={query}
              />
            </Form.Field>

            <LoadingModule loading={initialLoading} times={3} boxProps={{ marginTop: 'large' }} />

            {!initialLoading && (
            <>
              <DashboardModule title="Templates" boxProps={{ marginTop: 'large', marginBottom: 'none' }}>

                {templates.length === 0 && !loading && (
                <DashboardModule.BlankState>
                  <DashboardModule.BlankState.Title>
                    No Templates Present.
                  </DashboardModule.BlankState.Title>
                </DashboardModule.BlankState>
                )}

                {templates.length > 0 && (
                <Box flexDirection="column">
                  <Grid
                    // @ts-expect-error TS2322
                    gridGap="large"
                    gridAutoRows="1fr"
                    gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))"
                    gridTemplateRows="repeat(auto-fill, minmax(240px, 1fr))"
                    padding="large"
                  >
                    {templates.map(template => (
                      <CanvaTemplatePage
                        key={template.id}
                        isDisabled={creatingArtwork && selectedTemplateId !== template.id}
                        isLoading={creatingArtwork && selectedTemplateId === template.id}
                        callbacks={{
                          selectTemplate: (selectedTemplate) => {
                            if (creatingArtwork){
                              return
                            }

                            launchModal({
                              callbacks: pageCallbacks,
                              modalKey: 'CanvaArtworkingLoadingModal',
                              payload: {
                                modalTitle: 'Creating',
                                modalTitleSecondLine: 'Canva artwork',
                                title: 'Creating your Canva artwork',
                                // eslint-disable-next-line max-len
                                paragraph: 'Please wait while we are building your artwork in Canva. The Canva artwork will open in a new tab when the artwork is ready.',
                              },
                            })

                            setSelectedTemplateId(selectedTemplate.id)
                            setCreatingArtwork(true)
                            toast.info('Please wait... Artwork is being created.')
                            createArtwork({ brandTemplateId: selectedTemplate.id })
                          },
                        }}
                        template={template}
                      />
                    ))}
                  </Grid>
                </Box>
                )}
              </DashboardModule>

              {!initialLoading && hasTemplates && (
              <Box variant="white">
                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={templates.length}
                  loading={loading}
                  showBorderTop={false}
                />
              </Box>
              )}
            </>
            )}
          </Columns.Main>
        </Columns>
      </SidebarModal.Content>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CanvaTemplateSelectionModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
